// Renderiza descripciones con HTML SEGURO (allowlist) y convierte URLs
// sueltas en enlaces clicables. Permite incrustar imágenes (<img src=...>) y
// enlaces (<a href=...>) sin abrir la puerta a XSS: se eliminan scripts,
// atributos on*, estilos y cualquier URL que no sea http(s)/mailto.
//
// Si el texto no trae etiquetas HTML se trata como texto plano: se escapan
// los caracteres especiales, se linkifican las URLs y los saltos de línea se
// convierten en <br> (comportamiento equivalente al whitespace-pre-wrap previo).

const ALLOWED_TAGS = new Set([
  "a", "img", "b", "strong", "i", "em", "u", "s", "br", "p", "div", "span",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "code", "pre", "blockquote",
  "table", "thead", "tbody", "tr", "td", "th", "hr",
]);

const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

function safeHref(url) {
  try {
    const u = new URL(url, window.location.origin);
    return ["http:", "https:", "mailto:"].includes(u.protocol) ? u.href : null;
  } catch {
    return null;
  }
}

function safeSrc(url) {
  try {
    const u = new URL(url, window.location.origin);
    return ["http:", "https:"].includes(u.protocol) ? u.href : null;
  } catch {
    return null;
  }
}

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// Texto plano → HTML: escapar + linkificar + saltos de línea.
function plainToHtml(text) {
  const escaped = escapeHtml(text);
  const linked = escaped.replace(URL_RE, (m) => {
    const href = safeHref(m);
    return href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${m}</a>` : m;
  });
  return linked.replace(/\n/g, "<br>");
}

// Linkifica URLs que quedaron como texto dentro de nodos ya sanitizados.
function linkifyTextNodes(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets = [];
  let n;
  while ((n = walker.nextNode())) {
    // no tocar texto que ya está dentro de un <a>
    if (n.parentElement?.closest("a")) continue;
    if (URL_RE.test(n.nodeValue)) targets.push(n);
    URL_RE.lastIndex = 0;
  }
  for (const node of targets) {
    const span = document.createElement("span");
    span.innerHTML = node.nodeValue.replace(URL_RE, (m) => {
      const href = safeHref(m);
      return href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${escapeHtml(m)}</a>` : escapeHtml(m);
    });
    node.replaceWith(...span.childNodes);
  }
}

function sanitize(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (el) => {
    for (const child of [...el.children]) {
      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        // conservar el texto interior pero eliminar la etiqueta peligrosa;
        // script/style se eliminan COMPLETOS (su contenido es código).
        if (tag === "script" || tag === "style" || tag === "iframe" || tag === "object" || tag === "embed") {
          child.remove();
        } else {
          child.replaceWith(...child.childNodes);
          continue;
        }
        continue;
      }
      // limpiar atributos: solo los seguros por etiqueta
      for (const attr of [...child.attributes]) {
        const name = attr.name.toLowerCase();
        if (tag === "a" && name === "href") {
          const href = safeHref(attr.value);
          if (href) { child.setAttribute("href", href); continue; }
        }
        if (tag === "img" && (name === "src" || name === "alt" || name === "title" || name === "width" || name === "height")) {
          if (name === "src") {
            const src = safeSrc(attr.value);
            if (src) { child.setAttribute("src", src); continue; }
          } else { continue; }
        }
        child.removeAttribute(attr.name);
      }
      if (tag === "a") {
        child.setAttribute("target", "_blank");
        child.setAttribute("rel", "noopener noreferrer");
      }
      walk(child);
    }
  };
  walk(doc.body);
  linkifyTextNodes(doc.body);
  return doc.body.innerHTML;
}

export function renderRich(text) {
  if (!text) return "";
  // ¿trae etiquetas HTML? (heurística: <algo ...> o </algo>)
  const hasHtml = /<\/?[a-z][^>]*>/i.test(text);
  try {
    return hasHtml ? sanitize(text) : plainToHtml(text);
  } catch {
    return plainToHtml(text);
  }
}

export default function RichText({ text, className = "" }) {
  if (!text) return null;
  return (
    <div
      className={`rich-text break-words ${className}`}
      dangerouslySetInnerHTML={{ __html: renderRich(text) }}
    />
  );
}
